import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  Calendar,
  FileText,
  Award,
  ExternalLink,
  ArrowLeft,
  PlayCircle,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  X,
  Lock,
  Linkedin,
  MessageSquare,
  AlignLeft,
} from "lucide-react";
import { tokenService } from "../services/TokenService";
import {
  useCourseDetailsQuery,
  useCourseAssignmentsQuery,
  useCourseOverviewQuery,
  useCourseSkillsQuery,
  CourseModule,
  Assignment,
  Course,
} from "../api/studentCoursesApi";
import { AuthMeResponse } from "../api/authApi";
import LtiModuleCard from "../components/modules/LtiModuleCard";
import ModuleDetailModal from "../components/modules/ModuleDetailModal";
import { PageModuleContent } from "../components/modules/page";
import { QuizModuleContent } from "../components/modules/quiz";
import { LessonModuleContent } from "../components/modules/lesson";
import { BookModuleContent } from "../components/modules/book";
import { ForumModuleContent } from "../components/modules/forum";
import { AssignModuleContent } from "../components/modules/assign";
import { LabelModuleContent } from "../components/modules/label";
import ModuleCompleteButton from "../components/modules/common/ModuleCompleteButton";
import CourseCertificate from "../components/course/CourseCertificate";
import { useAuthMeQuery } from "../api/authApi";
import routes from "../routes";
import { usePermissions } from "../hooks/usePermissions";
import { PERMISSIONS } from "../utils/authUtils";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSettings } from "../api/settingsApi";

// Module/Content Item Component
interface ModuleItemProps {
  module: CourseModule;
  contextId: string;
  onProjectClick?: (projectId: string) => void;
  onModuleSelect?: (module: CourseModule) => void;
  isActive?: boolean;
}

function ModuleItem({
  module,
  contextId,
  onProjectClick,
  onModuleSelect,
  isActive,
}: ModuleItemProps) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const getModuleIcon = () => {
    switch (module.modname) {
      case "assign":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "resource":
        return <BookOpen className="h-5 w-5 text-green-500" />;
      case "url":
        return <ExternalLink className="h-5 w-5 text-purple-500" />;
      case "lti":
        return <PlayCircle className="h-5 w-5 text-indigo-500" />;
      case "quiz":
        return <GraduationCap className="h-5 w-5 text-orange-500" />;
      case "lesson":
        return <BookOpen className="h-5 w-5 text-purple-500" />;
      case "book":
        return <BookOpen className="h-5 w-5 text-teal-500" />;
      case "forum":
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case "label":
        return <AlignLeft className="h-5 w-5 text-slate-400" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const hasLinkedProject =
    module.linkedProject !== undefined && module.linkedProject !== null;

  // Handle LTI modules with linked projects using the dedicated component
  if (module.modname === "lti") {
    return (
      <LtiModuleCard
        module={module}
        onOpenProject={() => {
          if (module.linkedProject) {
            navigate(routes.toProjectDetails(module.linkedProject.id));
          }
        }}
      />
    );
  }

  // Inline-renderable module types
  const isInline = [
    "page",
    "quiz",
    "lesson",
    "book",
    "forum",
    "assign",
    "label",
  ].includes(module.modname);
  // Other types with modal
  const hasDetailedView =
    module.hasDetails && !["forum"].includes(module.modname);
  const isClickable = isInline || hasDetailedView || !!module.url;

  const handleModuleClick = () => {
    if (isInline) {
      onModuleSelect?.(module);
    } else if (hasDetailedView) {
      setShowModal(true);
    } else if (module.url) {
      window.open(module.url, "_blank");
    }
  };

  return (
    <>
      <div
        className={`bg-white border rounded-lg overflow-hidden shadow-sm transition-all ${
          isClickable ? "cursor-pointer hover:shadow-md" : ""
        } ${isActive ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200"}`}
        onClick={isClickable ? handleModuleClick : undefined}
      >
        {/* Header Section */}
        <div className="from-gray-50 to-white px-4 py-3 border-b border-gray-100 bg-linear-to-r">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              <div className="shrink-0">{getModuleIcon()}</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900 truncate">
                  {module.name}
                </h4>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              {module.visible === 0 && (
                <Badge variant="outline" className="text-xs">
                  Hidden
                </Badge>
              )}
              {module.modname && (
                <Badge variant="outline" className="text-xs capitalize">
                  {module.modname}
                </Badge>
              )}
              {module.modname === "quiz" && module.quizStatus && (
                <Badge
                  className={`text-xs capitalize ${
                    module.quizStatus === "submitted"
                      ? "bg-green-100 text-green-800 hover:bg-green-200"
                      : module.quizStatus === "in_progress"
                        ? "bg-blue-100 text-blue-800 hover:bg-blue-200"
                        : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                  variant="secondary"
                >
                  {module.quizStatus.replace("_", " ")}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        {(module.description ||
          (hasLinkedProject && module.modname !== "lti")) && (
          <div className="p-4 space-y-4">
            {module.description && (
              <div
                className="text-sm text-gray-700 leading-relaxed line-clamp-2"
                dangerouslySetInnerHTML={{ __html: module.description }}
              />
            )}

            {hasLinkedProject && module.modname !== "lti" && (
              <div className="bg-linear-to-br from-blue-50 via-indigo-50 to-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-blue-500 rounded-full p-1.5">
                        <PlayCircle className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                          Linked Project
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {module.linkedProject!.title}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-full">
                        <Award className="h-3 w-3 text-blue-600" />
                        <span className="text-gray-700 font-medium">
                          {module.linkedProject!.difficultyLevel}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-full">
                        <span className="text-gray-700">
                          <span className="font-semibold text-blue-600">
                            {module.linkedProject!.totalPoints}
                          </span>{" "}
                          points
                        </span>
                      </div>
                    </div>
                    {module.linkedProject!.progress && (
                      <div className="bg-white rounded-lg p-2.5">
                        <div className="flex justify-between items-center text-xs mb-1.5">
                          <span className="text-gray-600 font-medium">
                            Your Progress
                          </span>
                          <span className="font-bold text-blue-600">
                            {Math.round(
                              module.linkedProject!.progress.percentage,
                            )}
                            %
                          </span>
                        </div>
                        <Progress
                          value={module.linkedProject!.progress.percentage}
                          className="h-2"
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onProjectClick?.(module.linkedProject!.id);
                    }}
                    size="sm"
                    className="shrink-0 bg-blue-600 hover:bg-blue-700"
                  >
                    <PlayCircle className="h-3.5 w-3.5 mr-1" />
                    Open
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {hasDetailedView && (
        <ModuleDetailModal
          module={module}
          contextId={contextId}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// Assignment Item Component
interface AssignmentItemProps {
  assignment: Assignment;
  onProjectClick?: (projectId: string) => void;
}

function AssignmentItem({ assignment, onProjectClick }: AssignmentItemProps) {
  const getStatusBadge = () => {
    if (!assignment.linkedProject) {
      return <Badge variant="outline">No Project</Badge>;
    }

    switch (assignment.linkedProject.progress.status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case "in_progress":
        return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
      default:
        return <Badge variant="outline">Not Started</Badge>;
    }
  };

  const isOverdue =
    assignment.dueDate && new Date(assignment.dueDate) < new Date();

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">
              {assignment.name}
            </h3>
            {assignment.intro && (
              <div
                className="text-sm text-gray-600 line-clamp-2"
                dangerouslySetInnerHTML={{ __html: assignment.intro }}
              />
            )}
          </div>
          {getStatusBadge()}
        </div>

        {/* Assignment Details */}
        <div className="space-y-2">
          <div className="flex items-center gap-4 text-sm">
            {assignment.dueDate && (
              <div
                className={`flex items-center gap-1 ${isOverdue ? "text-red-600" : "text-gray-600"}`}
              >
                <Calendar className="h-4 w-4" />
                <span>
                  Due: {new Date(assignment.dueDate).toLocaleDateString()}
                </span>
                {isOverdue && <AlertCircle className="h-4 w-4 ml-1" />}
              </div>
            )}
            {assignment.maxGrade > 0 && (
              <div className="flex items-center gap-1 text-gray-600">
                <Award className="h-4 w-4" />
                <span>{assignment.maxGrade} points</span>
              </div>
            )}
          </div>

          {/* Linked Project */}
          {assignment.linkedProject && (
            <div className="mt-4 p-3 bg-linear-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-900">
                      Project: {assignment.linkedProject.title}
                    </span>
                  </div>

                  {assignment.linkedProject.progress.percentage > 0 && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-medium text-gray-900">
                          {Math.round(
                            assignment.linkedProject.progress.percentage,
                          )}
                          %
                        </span>
                      </div>
                      <Progress
                        value={assignment.linkedProject.progress.percentage}
                        className="h-2"
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                    <span>
                      Difficulty: {assignment.linkedProject.difficultyLevel}
                    </span>
                    {assignment.linkedProject.progress.lastActivity && (
                      <span>
                        Last worked:{" "}
                        {new Date(
                          assignment.linkedProject.progress.lastActivity,
                        ).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => onProjectClick?.(assignment.linkedProject!.id)}
                  size="sm"
                  className="ml-3"
                >
                  <PlayCircle className="h-4 w-4 mr-1" />
                  Start
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Inline module content renderer
function InlineModuleContent({
  module,
  contextId,
  headerColor,
}: {
  module: CourseModule;
  contextId: string;
  headerColor?: string;
}) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  console.log("module.modname", module.modname);
  switch (module.modname) {
    case "assign":
      return (
        <AssignModuleContent
          contextId={contextId}
          moduleId={module.id.toString()}
          module={module}
          headerColor={headerColor}
        />
      );
    case "page":
      return (
        <PageModuleContent
          contextId={contextId}
          moduleId={module.id.toString()}
          module={module}
          headerColor={headerColor}
        />
      );
    case "quiz":
      return (
        <QuizModuleContent
          contextId={contextId}
          moduleId={module.id.toString()}
          module={module}
          headerColor={headerColor}
        />
      );
    case "lesson":
      return (
        <LessonModuleContent
          contextId={contextId}
          moduleId={module.id.toString()}
          module={module}
        />
      );
    case "book":
      return (
        <BookModuleContent
          contextId={contextId}
          moduleId={module.id.toString()}
          module={module}
          headerColor={headerColor}
        />
      );
    case "forum":
      return (
        <ForumModuleContent
          contextId={contextId}
          moduleId={module.id.toString()}
          module={module}
          headerColor={headerColor}
        />
      );
    case "label":
      return (
        <LabelModuleContent
          contextId={contextId}
          moduleId={module.id.toString()}
          module={module}
        />
      );
    default:
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-8 text-center">
          <FileText className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <p className="font-medium text-gray-700">{module.name}</p>
          {module.description && (
            <div
              className="text-sm text-gray-500 mt-2"
              dangerouslySetInnerHTML={{ __html: module.description }}
            />
          )}
          {module.url && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={(e) => {
                e.stopPropagation();

                // If module has an explicit linkedProject, prefer internal navigation
                if (
                  (module as any).linkedProject &&
                  (module as any).linkedProject.id
                ) {
                  navigate(
                    routes.toProjectDetails((module as any).linkedProject.id),
                  );
                  return;
                }

                // Try to extract a project id from the URL path (common patterns)
                try {
                  const url = String(module.url);
                  const pathMatch = url.match(
                    /\/(?:practice-projects|practice-projects|practice-project|project|projects|project-details|view-project-details)\/([A-Za-z0-9-_]+)/,
                  );
                  if (pathMatch && pathMatch[1]) {
                    navigate(routes.toProjectDetails(pathMatch[1]));
                    return;
                  }

                  // Try query params like ?projectId=... or ?id=...
                  const parsed = new URL(url, window.location.origin);
                  const qId =
                    parsed.searchParams.get("projectId") ||
                    parsed.searchParams.get("id") ||
                    parsed.searchParams.get("sess");
                  if (qId) {
                    navigate(routes.toProjectDetails(qId));
                    return;
                  }
                } catch (err) {
                  // ignore and fallback
                }

                // Permission fallback: if user lacks project view permission, open external link as before
                if (!hasPermission(PERMISSIONS.VIEW_PROJECTS)) {
                  window.open(module.url, "_blank");
                  return;
                }

                // Final fallback: open external URL in new tab
                window.open(module.url, "_blank");
              }}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Project
            </Button>
          )}
          </div>
          {/* Footer with Mark as Complete. Resources, files and URLs aren't
              tracked by Moodle, so this records portal-local completion —
              without it these module types can never be marked done. */}
          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 flex justify-end">
            <ModuleCompleteButton
              contextId={contextId}
              moduleId={module.id.toString()}
              variant="footer"
            />
          </div>
        </div>
      );
  }
}

// Sidebar module icon helper
function getSidebarModuleIcon(modname: string) {
  switch (modname) {
    case "assign":
      return <FileText className="h-4 w-4 shrink-0" />;
    case "resource":
      return <BookOpen className="h-4 w-4 shrink-0" />;
    case "url":
      return <ExternalLink className="h-4 w-4 shrink-0" />;
    case "lti":
      return <PlayCircle className="h-4 w-4 shrink-0" />;
    case "quiz":
      return <GraduationCap className="h-4 w-4 shrink-0" />;
    case "lesson":
      return <BookOpen className="h-4 w-4 shrink-0" />;
    case "book":
      return <BookOpen className="h-4 w-4 shrink-0" />;
    case "page":
      return <FileText className="h-4 w-4 shrink-0" />;
    case "forum":
      return <MessageSquare className="h-4 w-4 shrink-0" />;
    case "label":
      return <AlignLeft className="h-4 w-4 shrink-0" />;
    default:
      return <FileText className="h-4 w-4 shrink-0" />;
  }
}

// Returns Tailwind color classes matching each modname (mirrors getModuleIcon colors)
function getSidebarModuleColor(modname: string): {
  icon: string;
  activeBg: string;
  activeText: string;
  activeBorder: string;
} {
  switch (modname) {
    case "assign":
      return {
        icon: "text-blue-500",
        activeBg: "bg-blue-50",
        activeText: "text-blue-700",
        activeBorder: "border-l-blue-500",
      };
    case "resource":
      return {
        icon: "text-green-500",
        activeBg: "bg-green-50",
        activeText: "text-green-700",
        activeBorder: "border-l-green-500",
      };
    case "url":
      return {
        icon: "text-purple-500",
        activeBg: "bg-purple-50",
        activeText: "text-purple-700",
        activeBorder: "border-l-purple-500",
      };
    case "lti":
      return {
        icon: "text-indigo-500",
        activeBg: "bg-indigo-50",
        activeText: "text-indigo-700",
        activeBorder: "border-l-indigo-500",
      };
    case "quiz":
      return {
        icon: "text-orange-500",
        activeBg: "bg-orange-50",
        activeText: "text-orange-700",
        activeBorder: "border-l-orange-500",
      };
    case "lesson":
      return {
        icon: "text-purple-500",
        activeBg: "bg-purple-50",
        activeText: "text-purple-700",
        activeBorder: "border-l-purple-500",
      };
    case "book":
      return {
        icon: "text-teal-500",
        activeBg: "bg-teal-50",
        activeText: "text-teal-700",
        activeBorder: "border-l-teal-500",
      };
    case "page":
      return {
        icon: "text-blue-500",
        activeBg: "bg-blue-50",
        activeText: "text-blue-700",
        activeBorder: "border-l-blue-500",
      };
    case "forum":
      return {
        icon: "text-blue-500",
        activeBg: "bg-blue-50",
        activeText: "text-blue-700",
        activeBorder: "border-l-blue-500",
      };
    case "label":
      return {
        icon: "text-slate-400",
        activeBg: "bg-slate-50",
        activeText: "text-slate-600",
        activeBorder: "border-l-slate-400",
      };
    default:
      return {
        icon: "text-gray-400",
        activeBg: "bg-gray-50",
        activeText: "text-gray-700",
        activeBorder: "border-l-gray-400",
      };
  }
}

// Main Course Details Page
export default function CourseDetailsPage() {
  const { contextId } = useParams<{ contextId: string }>();
  const navigate = useNavigate();
  const [token] = useState(tokenService.getToken());
  const [activeModule, setActiveModule] = useState<CourseModule | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set(),
  );
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);

  const location = useLocation();
  const courseFromState = location.state?.course as Course | undefined;
  console.log(courseFromState, "courseFromState");
  const toggleSection = (sectionId: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const { data: courseContent, isLoading: contentLoading } =
    useCourseDetailsQuery(contextId, token);
  const { data: courseAssignments, isLoading: assignmentsLoading } =
    useCourseAssignmentsQuery(contextId, token);
  const { data: courseOverview, isLoading: overviewLoading } =
    useCourseOverviewQuery(contextId, token);
  const { data: courseSkills } = useCourseSkillsQuery(contextId, token);
  const { data: authMeRaw } = useAuthMeQuery();
  const authMe = authMeRaw as unknown as AuthMeResponse | undefined;

  const settingsQuery = useQuery({
    queryKey: ["/settings"],
    queryFn: getSettings,
    refetchOnWindowFocus: false,
  });
  const sidebarColor = settingsQuery?.data?.data?.appearance?.primaryColor as
    | string
    | undefined;

  const allExpanded = courseContent
    ? expandedSections.size >= courseContent.sections.length
    : false;

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedSections(new Set());
    } else {
      setExpandedSections(
        new Set(courseContent?.sections.map((s) => s.id) ?? []),
      );
    }
  };

  const decodeHtml = (str: string) =>
    str
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

  const skills: string[] = courseSkills
    ? courseSkills
        .split(/\r?\n/)
        .map((s) => decodeHtml(s.trim()).replace(/^✅\s*/, ""))
        .filter(Boolean)
    : [];

  const studentName = authMe?.user?.name || "Student";

  // Derive course progress from live completion-tracked modules so it updates
  // as soon as a topic is marked complete. Fall back to the stats snapshot
  // passed via navigation state when no tracked modules are available yet.
  const trackedModules =
    courseContent?.sections
      .flatMap((s) => s.modules)
      .filter((m) => m.completionTracked) ?? [];
  const completedModuleCount = trackedModules.filter((m) => m.completed).length;
  const computedCompletionPercentage =
    trackedModules.length > 0
      ? Math.round((completedModuleCount / trackedModules.length) * 100)
      : null;
  const completionPercentage =
    computedCompletionPercentage ??
    courseFromState?.stats?.completionPercentage ??
    0;
  const isCompleted = completionPercentage >= 100;
  // Capture before JSX ternary so TypeScript doesn't narrow it to null inside the overview branch
  const activeModuleId = activeModule?.id ?? null;

  // Expand all sections and auto-select the first module — but only once per
  // course. Marking a module complete patches the courseDetails cache, which
  // produces a new `courseContent` object; if this ran on every change it would
  // reset `activeModule` back to the first module (typically the Announcements
  // forum) and yank the student out of the topic they just completed.
  const initializedContextRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      courseContent &&
      courseContent.sections.length > 0 &&
      initializedContextRef.current !== contextId
    ) {
      initializedContextRef.current = contextId ?? null;
      setExpandedSections(new Set(courseContent.sections.map((s) => s.id)));
      const firstModule = courseContent.sections[0]?.modules?.[0] ?? null;
      if (firstModule) setActiveModule(firstModule);
    }
  }, [courseContent, contextId]);

  const handleBack = () => navigate("/my-courses");

  const findModuleUrlByProjectId = (projectId: string): string | null => {
    if (!courseContent) return null;
    for (const section of courseContent.sections) {
      for (const mod of section.modules) {
        if (
          mod.linkedProject &&
          String(mod.linkedProject.id) === String(projectId)
        ) {
          return mod.url || null;
        }
      }
    }
    return null;
  };

  const handleProjectClick = async (projectId: string) => {
    try {
      const res = await fetch(`/api/projects/${projectId}/details`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (res.ok) {
        navigate(`/project-details/${projectId}`, {
          state: { fromCourse: contextId },
        });
        return;
      }

      // If backend denies access, fallback to module URL (old flow)
      const fallbackUrl = findModuleUrlByProjectId(projectId);
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank");
        return;
      }

      console.warn(
        "Cannot open project - access denied and no fallback URL available.",
      );
    } catch (err) {
      console.error("Error checking project access:", err);
      const fallbackUrl = findModuleUrlByProjectId(projectId);
      if (fallbackUrl) window.open(fallbackUrl, "_blank");
    }
  };

  const isLoading = contentLoading || assignmentsLoading || overviewLoading;

  const courseTitle =
    courseContent?.courseTitle ||
    courseAssignments?.courseTitle ||
    "Course Details";

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-50 overflow-hidden z-40">
      {/* ── Full-width Sticky Header ── */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors group shrink-0"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back
        </button>
        <div className="h-5 w-px bg-gray-200 shrink-0" />
        <div className="flex-1 min-w-0 flex flex-col items-center justify-center text-center">
          {isLoading ? (
            <span className="block h-5 w-48 bg-gray-200 rounded animate-pulse" />
          ) : (
            <div className="flex items-baseline gap-3">
              <div className="text-3xl font-bold text-gray-900 leading-tight truncate">
                {courseTitle}
              </div>
              {courseFromState?.label && (
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wider shrink-0">
                  - {courseFromState.label}
                </span>
              )}
            </div>
          )}
        </div>
        {/* Spacer to balance the back button */}
        <div className="shrink-0 w-16" />
      </div>

      {/* ── Body (sidebar + main) ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Left Sidebar ── */}
        <aside className="w-96 shrink-0 bg-white border-r border-gray-200 flex flex-col">
          {/* Table of Contents */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-2.5 border-b border-gray-100 bg-white sticky top-0 z-10 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Course Content
              </h3>
              <button
                onClick={() => isCompleted && setShowCertificate(true)}
                disabled={!isCompleted}
                title={
                  isCompleted
                    ? "Share your achievement on LinkedIn"
                    : `${completionPercentage}% complete — finish the course to unlock`
                }
                className={`flex items-center justify-center w-7 h-7 rounded-sm transition-all ${
                  isCompleted
                    ? "bg-[#0a66c2] text-white shadow-sm hover:shadow-md active:scale-95 cursor-pointer"
                    : "bg-gray-100 text-gray-300 cursor-not-allowed"
                }`}
              >
                {isCompleted ? (
                  <Linkedin className="h-3.5 w-3.5" />
                ) : (
                  <Lock className="h-3.5 w-3.5" />
                )}
              </button>
            </div>

            {/* Course progress bar — reflects completed topics */}
            {!isLoading && computedCompletionPercentage !== null && (
              <div className="px-4 py-3 border-b border-gray-100 bg-white">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-medium text-gray-600">Your Progress</span>
                  <span className="font-semibold text-gray-900">
                    {completedModuleCount}/{trackedModules.length} topics ·{" "}
                    {completionPercentage}%
                  </span>
                </div>
                <Progress value={completionPercentage} className="h-2" />
              </div>
            )}

            {isLoading ? (
              <div className="p-4 space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-8 bg-gray-100 rounded animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <nav>
                {courseContent?.sections.map((section) => (
                  <div key={section.id}>
                    {/* Section header */}
                    <div className="px-4 py-2 bg-gray-100 border-y border-gray-200">
                      <span className="text-[11px] font-bold text-gray-600 uppercase tracking-widest">
                        {section.name || "General"}
                      </span>
                    </div>
                    {/* Module items */}
                    {section.modules.map((mod) => {
                      const isActive = mod.id === activeModule?.id;
                      const colors = getSidebarModuleColor(mod.modname);
                      return (
                        <button
                          key={mod.id}
                          onClick={() => setActiveModule(mod)}
                          style={
                            isActive && sidebarColor
                              ? {
                                  borderLeftColor: sidebarColor,
                                  backgroundColor: `${sidebarColor}14`,
                                  color: sidebarColor,
                                }
                              : undefined
                          }
                          className={`w-full text-left flex items-start gap-2.5 px-4 py-2 border-b border-gray-50 transition-colors text-sm ${
                            isActive
                              ? `${!sidebarColor ? colors.activeBg : ""} border-l-2 ${!sidebarColor ? colors.activeBorder : ""} ${!sidebarColor ? colors.activeText : ""} font-medium pl-3.5`
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          }`}
                        >
                          <span
                            className={`mt-0.5 shrink-0 ${isActive && !sidebarColor ? colors.icon : isActive ? "" : "text-gray-400"}`}
                            style={
                              isActive && sidebarColor
                                ? { color: sidebarColor }
                                : undefined
                            }
                          >
                            {getSidebarModuleIcon(mod.modname)}
                          </span>
                          <span className="leading-snug line-clamp-2">
                            {mod.name}
                          </span>
                          {mod.completed && (
                            <span className="ml-2 mt-0.5 flex items-center text-green-600 shrink-0">
                              <CheckCircle2 className="h-4 w-4" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </nav>
            )}
          </div>

          {/* ── Skills You'll Gain accordion (pinned to bottom of sidebar) ── */}
          {skills.length > 0 && (
            <div className="shrink-0 border-t border-gray-200">
              <button
                onClick={() => setSkillsOpen((o) => !o)}
                className="w-full flex items-center justify-between px-5 py-3 bg-slate-900"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-slate-300" />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-white">
                    Skills You'll Gain
                  </span>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                    skillsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {skillsOpen && (
                <div className="bg-slate-50 px-4 py-3 border-t border-slate-100">
                  <style>{`
                  @keyframes skillFadeUp {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                  }
                `}</style>
                  <div className="flex flex-wrap gap-1.5">
                    {skills.map((skill, i) => (
                      <span
                        key={skill}
                        style={{
                          animation: "skillFadeUp 0.35s ease forwards",
                          animationDelay: `${i * 60}ms`,
                          opacity: 0,
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white border border-slate-200 text-slate-700 shadow-sm"
                      >
                        <CheckCircle2 className="h-3 w-3 text-indigo-400 shrink-0" />
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 overflow-y-auto px-8 py-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
                <p className="text-gray-600">Loading course details...</p>
              </div>
            </div>
          ) : activeModule ? (
            /* ── Inline Module Content ── */
            <InlineModuleContent
              module={activeModule}
              contextId={contextId || ""}
              headerColor={sidebarColor}
            />
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400">
              <p>Select a module from the sidebar to get started.</p>
            </div>
          )}
        </main>
      </div>
      {/* end body row */}
      {/* ── Certificate Modal ── */}
      {showCertificate && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-start justify-center p-8 overflow-y-auto"
          onClick={() => setShowCertificate(false)}
        >
          <div
            className="relative w-full max-w-4xl mt-6 mb-12"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCertificate(false)}
              className="absolute -top-9 right-0 flex items-center gap-1.5 text-white/70 hover:text-white text-sm transition-colors"
            >
              <X className="h-4 w-4" />
              Close
            </button>
            <CourseCertificate
              courseTitle={courseTitle}
              studentName={studentName}
              skills={skills}
              isUnlocked={isCompleted}
              completionPercentage={completionPercentage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
